import React, { useState } from 'react';
import { GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import { HeaderThElement } from '@progress/kendo-react-data-tools';
import {
  Box,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableSortLabel,
  Tooltip,
} from '@mui/material';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSelectedAssemblyTemplateId } from '../../hooks';
import { useHavePermission } from '@/hooks/useHavePermissions';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Permissions } from '@/types';
import {
  useDeletePlannerColumnMutation,
  AllAssemblyTemplatesForFilterSideBarDocument,
  TemplateTypeEnum,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { DeleteOutline, Edit, MoreVert } from '@mui/icons-material';
import { Columns } from '@/types';
import { abyssColors, primary } from '@/theme/colors';
import { getColumnDimensions } from './utils';
import { DeleteConfirmationModal } from '@/components/shared/DeleteConfirmationModal/DeleteConfirmationModal';

interface CustomHeaderCellProps extends GridCustomHeaderCellProps {
  column: Columns;
  columnIndex: number;
  totalColumns: number;
  isFirstColumn: boolean;
  isLastColumn: boolean;
  onSort?: (columnKey: string) => void;
  onToggleColumnDialog?: () => void;
  onHeaderCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onOpenHeaderEditModal?: (columnKey: string, columnName: string) => void;
  onOpenColumnModal?: (column: Columns) => void;
  selectedRowIds?: string[];
  isChecked?: boolean;
  onDeleteColumn?: (columnKey: string, columnName: string) => void | Promise<void>;
  orderBy?: string;
  order?: number;
}

export const CustomHeaderCell: React.FC<CustomHeaderCellProps> = (props) => {
  const {
    column,
    totalColumns,
    isFirstColumn,
    onSort,
    onHeaderCheckboxChange,
    onOpenHeaderEditModal,
    onOpenColumnModal,
    selectedRowIds = [],
    isChecked = false,
    onDeleteColumn,
    orderBy = '',
    order = 1,
  } = props;
  const hasChildren = React.Children.toArray(props.children).filter(Boolean).length > 0;
  const isView3D = column.key === 'view3D';
  const dimensions = getColumnDimensions(column, totalColumns);
  const canBulkEdit = selectedRowIds.length > 0;
  const sortBy = column.sortKey || column.key;
  const isSorted = orderBy === sortBy;
  const isPlanner = column.isPlannerColumn || column.key.startsWith('planner');


  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const isHeaderMenuOpen = Boolean(headerMenuAnchorEl);

  const structureId = useGetStructureId();
  const selectedTemplateId = useSelectedAssemblyTemplateId();
  const [deletePlannerColumnMutation] = useDeletePlannerColumnMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const isAdmin = useFeatureFlag('admin');
  const canManageInspectionPlans = useHavePermission(Permissions.MANAGE_INSPECTION_PLANS);

  const deleteItemName = React.useMemo(() => {
    if (!column.columnName) return 'this column';
    return `“${column.columnName}” column`;
  }, [column.columnName]);

  const editDisabled = !column.enableInlineEdit || !canBulkEdit;
  const showSelectRowsTooltip = !canBulkEdit;

  const handleOpenHeaderMenu = (event: React.MouseEvent<HTMLElement>) => {
    setHeaderMenuAnchorEl(event.currentTarget);
  };

  const handleCloseHeaderMenu = () => {
    setHeaderMenuAnchorEl(null);
  };

  const handleHeaderMenuEdit = () => {
    handleCloseHeaderMenu();
    if (!canBulkEdit || column.enableInlineEdit !== true) return;
    if (column.key && onOpenHeaderEditModal) {
      onOpenHeaderEditModal(column.key, column.columnName);
    }
  };

  const handleHeaderMenuEditOption = () => {
    handleCloseHeaderMenu();
    if (onOpenColumnModal) {
      onOpenColumnModal(column);
      return;
    }
    if (column.key && onOpenHeaderEditModal) {
      onOpenHeaderEditModal(column.key, column.columnName);
    }
  };

  const handleHeaderMenuDelete = () => {
    handleCloseHeaderMenu();
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
  };

  const handleConfirmDeleteColumn = async () => {
    if (!onDeleteColumn) {

      if (!structureId || !selectedTemplateId || !column.key) {
        showSnackBar('Unable to delete column. Missing required data.', 'error');
        setDeleteModalOpen(false);
        return;
      }

      try {
        setDeleteLoading(true);
        await deletePlannerColumnMutation({
          variables: {
            input: {
              structureId,
              templateId: selectedTemplateId,
              fieldKey: String(column.key),
              type: TemplateTypeEnum.Assembly,
            },
          },
          refetchQueries: [AllAssemblyTemplatesForFilterSideBarDocument],
        });
        showSnackBar('Column deleted', 'success');
      } catch (error) {
        console.error(error);
      } finally {
        setDeleteLoading(false);
      }
      return;
    }

    if (!column.key) {
      setDeleteModalOpen(false);
      return;
    }

    try {
      setDeleteLoading(true);
      await onDeleteColumn(String(column.key), column.columnName);
      setDeleteModalOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const baseHeaderStyle: React.CSSProperties = {
    padding: dimensions.padding,
    alignItems: 'center',
    border: '1px solid rgba(224, 224, 224, 224)',
    backgroundColor: isPlanner ? abyssColors.primary[50] : '#fff',
    boxSizing: 'border-box',
  };

  const headerStyle: React.CSSProperties = isView3D
    ? {
      ...baseHeaderStyle,
      position: 'sticky',
      right: 0,
      zIndex: 3,
      boxShadow: '-4px 0px 4px 0px rgba(0, 0, 0, 0.05)',
    }
    : baseHeaderStyle;

  const thProps = props.thProps ?? {};
  const mergedStyle: React.CSSProperties = { ...(thProps as React.ThHTMLAttributes<HTMLTableCellElement>).style, ...headerStyle };
  const mergedClassName = `${(thProps as React.ThHTMLAttributes<HTMLTableCellElement>).className || ''} ${isView3D ? 'sticky-right-header equipment-header-cell' : 'equipment-header-cell'}`.trim();

  return (
    <HeaderThElement columnId={column.key} {...thProps} style={mergedStyle} className={mergedClassName}>
      <DeleteConfirmationModal
        title={'Are you sure ?'}
        isOpen={deleteModalOpen}
        itemName={deleteItemName}
        onConfirm={handleConfirmDeleteColumn}
        onCancel={handleCloseDeleteModal}
        handleClose={handleCloseDeleteModal}
        loading={
          deleteLoading
            ? {
              state: true,
              text: 'Deleting...',
            }
            : undefined
        }
      />

      <Menu
        anchorEl={headerMenuAnchorEl}
        open={isHeaderMenuOpen}
        onClose={handleCloseHeaderMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showSelectRowsTooltip ? (
          <Tooltip title="Select rows to enable bulk edit" placement="left" arrow>
            <span style={{ display: 'inline-block' }}>
              <MenuItem onClick={handleHeaderMenuEdit} disabled={editDisabled}>
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Bulk edit" />
              </MenuItem>
            </span>
          </Tooltip>
        ) : (
          <MenuItem onClick={handleHeaderMenuEdit} disabled={editDisabled}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit" />
          </MenuItem>
        )}

        {isPlanner && (isAdmin || canManageInspectionPlans) && (
          <MenuItem onClick={handleHeaderMenuEditOption}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
            <ListItemText primary="Edit column option" />
          </MenuItem>
        )}
        {isPlanner && (isAdmin || canManageInspectionPlans) && (
          <MenuItem onClick={handleHeaderMenuDelete}>
            <ListItemIcon>
              <DeleteOutline fontSize="small" sx={{ color: abyssColors.primary[500] }} />
            </ListItemIcon>
            <ListItemText primary="Delete Column" />
          </MenuItem>
        )}
      </Menu>

      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: '1 1 auto',
            minWidth: 0,
            gap: 0.5,
          }}
        >
          {isFirstColumn && (
            <Checkbox
              onChange={onHeaderCheckboxChange}
              sx={{
                color: primary.light,
                padding: { xs: 0.5, md: 0.5, lg: 1 },
              }}
              checked={isChecked}
              disableRipple
            />
          )}

          <Box
            sx={{
              fontWeight: 600,
              fontSize: '1.4rem',
              lineHeight: '16px',
              letterSpacing: '0.5px',
              color: abyssColors.primary[700],
              cursor: column.isSortable ? 'pointer' : 'default',
              overflow: 'hidden',
              textAlign: 'center',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {hasChildren ? props.children : column.columnName}
          </Box>

          {column.isSortable && (
            <TableSortLabel
              active={isSorted}
              direction={order === -1 ? 'desc' : 'asc'}
              disabled={!column.isSortable}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onSort?.(sortBy);
              }}
              sx={{
                ml: 0,
                '& .MuiTableSortLabel-icon': {
                  opacity: 1,
                  color: isSorted ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.38)',
                  fill: isSorted ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.38)',
                },
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            ml: 1,
          }}
        >
          {(column.enableInlineEdit || column.isPlannerColumn) && (
            <IconButton aria-label={`${column.columnName}-menu`} onClick={handleOpenHeaderMenu} sx={{ p: 0.5 }}>
              <MoreVert sx={{ fontSize: '1.8rem', color: abyssColors.primary[500] }} />
            </IconButton>
          )}
        </Box>
      </Box>
    </HeaderThElement>
  );
};
